import { ReactComponent as CloseIcon } from "assets/images/ico_close_m.svg";
import ReactModal from "react-modal";
import styled from "styled-components";
import { useQuery } from "react-query";
import useHttpClient, { api_v_url } from "hooks/useHttpClient";
import useAuth from "utils/auth";

const customStyles = {
  overlay: {
    zIndex: 1000,
    backgroundColor: "#00000070",
  },
  content: {
    width: "90%",
    maxWidth: "500px",
    inset: "unset",
    margin: "50vh auto",
    padding: 0,
    transform: "translateY(-50%)",
    position: "relative",
    borderRadius: "12px",
  },
};

const ModalWrapper = styled.div`
  border-radius: 12px;
  font-family: Pretendard;
  text-align: left;
  background: #ffffff;
`;

const ModalHeader = styled.div`
  padding: 24px 24px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 20px;
  font-weight: 700;
  line-height: 24px;
  border-bottom: 1px solid #e7e9ef;

  svg {
    cursor: pointer;
    width: 24px;
    height: 24px;
  }
`;

const ModalBody = styled.div`
  padding: 24px;
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 400;
  line-height: 22px;
  text-align: left;
  color: #141414;

  .promotion-content {
    margin-bottom: 24px;
  }

  .promotion-title {
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 12px;
    color: #141414;
  }

  .promotion-description {
    font-size: 15px;
    line-height: 22px;
    color: #4b4c4d;
    margin-bottom: 12px;
  }
`;

const PromotionList = styled.div`
  margin-top: 12px;
  margin-bottom: 12px;
`;

const PromotionItem = styled.div`
  font-size: 15px;
  line-height: 22px;
  color: #4b4c4d;
  margin-bottom: 8px;
  display: flex;
  align-items: flex-start;

  &.applied {
    color: #22c55e;
    font-weight: 600;
  }

  &.not-applied {
    color: #ef4444;
    font-weight: 500;
  }

  .status-icon {
    margin-right: 8px;
    flex-shrink: 0;
    width: 16px;
    text-align: center;
  }

  .loading-spinner {
    display: inline-block;
    width: 12px;
    height: 12px;
    border: 2px solid #e7e9ef;
    border-top-color: #ff3d6c;
    border-radius: 50%;
    animation: spin 0.6s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const ModalFooter = styled.div`
  padding: 16px 24px 24px;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  border-top: 1px solid #e7e9ef;
`;

const Button = styled.button`
  padding: 10px 20px;
  border-radius: 8px;
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 600;
  line-height: 17px;
  cursor: pointer;
  border: none;
  transition: all 0.2s ease;

  &:hover {
    opacity: 0.9;
  }

  &.secondary {
    background: #f5f5f5;
    color: #4b4c4d;

    &:hover {
      background: #e7e9ef;
    }
  }

  &.primary {
    background: #ff3d6c;
    color: #ffffff;

    &:hover {
      background: #e6355a;
    }
  }
`;

const PromotionModal = ({ isOpen, onClose, onDontShowForWeek }) => {
  const { isLoggedIn } = useAuth();
  const httpClient = useHttpClient(api_v_url);

  const { data: promotionStatus, isLoading } = useQuery(
    ["promotion-status", isOpen],
    async () => {
      const { data } = await httpClient.get("/v1b/events/event-2025-11-10");
      return data.data; // [boolean, boolean, boolean, boolean]
    },
    {
      enabled: isLoggedIn && isOpen,
      staleTime: 0, // 항상 최신 데이터 조회
      cacheTime: 0, // 캐시 사용 안함
      refetchOnMount: true, // 마운트 시 항상 재조회
      retry: false,
    }
  );

  const promotions = [
    { text: "회원가입 시 1주일 체험", applied: promotionStatus?.[0] },
    { text: "내 정보에서 이메일 인증 시 1주일 혜택", applied: promotionStatus?.[1] },
    { text: "키움증권 Open API 키 등록 시 1주일 체험", applied: promotionStatus?.[2] },
    { text: "가입 14일 후 접속 시 1주일 추가 체험", applied: promotionStatus?.[3] },
  ];

  return (
    <ReactModal
      isOpen={isOpen}
      style={customStyles}
      onRequestClose={onClose}
      ariaHideApp={false}
    >
      <ModalWrapper>
        <ModalHeader>
          <div>공지사항 및 미션 안내</div>
          <CloseIcon onClick={onClose} />
        </ModalHeader>
        <ModalBody>
          <div className="promotion-content">
            <div className="promotion-title">🎉 공지사항 </div>
            <div className="notice-description">
              테스트 문구 추가.
              서버의 IP 이전으로 인해 서비스가 불안정할 수 있습니다.
              <br />
              최대한 빠르게 서비스를 복구하도록 노력하겠습니다.
            </div>
          </div>
          <div className="promotion-content">
            <div className="promotion-title">🎉 특별 미션 진행 중!</div>
            <div className="promotion-description">
              키움증권 자동 업로드 혜택을 최대 4주 드립니다.
            </div>
            <PromotionList>
              {promotions.map((promo, index) => {
                let statusIcon = "•";
                let itemClassName = "";
                let statusText = "";

                if (isLoggedIn) {
                  if (isLoading) {
                    statusIcon = (
                      <span className="loading-spinner" />
                    );
                  } else if (promo.applied === true) {
                    statusIcon = "✓";
                    itemClassName = "applied";
                    statusText = " 적용 완료";
                  } else if (promo.applied === false) {
                    statusIcon = "✗";
                    itemClassName = "not-applied";
                  }
                }

                return (
                  <PromotionItem
                    key={index}
                    className={itemClassName}
                  >
                    <span className="status-icon">
                      {statusIcon}
                    </span>
                    <span>
                      {promo.text}
                      {statusText}
                    </span>
                  </PromotionItem>
                );
              })}
            </PromotionList>
            <div className="promotion-description">
              지금 바로 시작해보세요!
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button className="secondary" onClick={onDontShowForWeek}>
            1주일 동안 안보기
          </Button>
          <Button className="primary" onClick={onClose}>
            확인
          </Button>
        </ModalFooter>
      </ModalWrapper>
    </ReactModal>
  );
};

export default PromotionModal;

